import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Save, Sparkles, Loader2 } from "lucide-react"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/context/auth/auth-context"
import hive from "@/lib/api/hive"
import { type Position } from "@/types/portfolio"
import { getRandomPortfolioName } from "@/lib/portfolio-utils"
import { Analytics } from "@/lib/analytics"

interface SavePortfolioDialogProps {
	positions: Position[]
	onSuccess?: () => void
}

export function SavePortfolioDialog({ positions, onSuccess }: SavePortfolioDialogProps) {
	const { isAuthenticated, token } = useAuth()
	const navigate = useNavigate()
	const location = useLocation()

	const [open, setOpen] = useState(false)
	const [name, setName] = useState("")
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleOpenChange = (newOpen: boolean) => {
		if (newOpen) {
			if (!isAuthenticated) {
				navigate("/login", { state: { from: location } })
				return
			}
			setName(getRandomPortfolioName())
			setError(null)
		}
		setOpen(newOpen)
	}

	const handleRandomizeName = () => {
		setName(getRandomPortfolioName())
	}

	const handleSave = async () => {
		if (!name.trim()) {
			setError("Please enter a name for your portfolio")
			return
		}

		if (!token) return

		setIsLoading(true)
		setError(null)

		try {
			// First, create the portfolio
			const newPortfolio = await hive.createPortfolio({ name }, token)

			// To maintain "save" functionality from Demo, we should add transactions
			// for each position.
			await Promise.all(
				positions.map((pos) =>
					hive.createTransaction(
						newPortfolio.id,
						{
							ticker: pos.ticker,
							exchange: pos.exchange,
							type: "BUY",
							shares: pos.shares,
							// Use existing price if available, otherwise backend will fetch current
							price: pos.price,
							date: new Date().toISOString(),
						},
						token,
					),
				),
			)

			Analytics.capture("Demo Saved to Account")
			setOpen(false)
			if (onSuccess) onSuccess()
			navigate("/dashboard")
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to save portfolio")
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button className="bg-sky-600 hover:bg-sky-700 text-white gap-2 shadow-lg shadow-sky-600/20">
					<Save className="h-4 w-4" />
					Save Portfolio
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Save to Your Hive</DialogTitle>
					<DialogDescription>Give your portfolio a name to save it to your account.</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="name">Portfolio Name</Label>
						<div className="flex gap-2">
							<Input
								id="name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g. My Retirement Hive"
								className="flex-1"
							/>
							<Button
								type="button"
								variant="outline"
								size="icon"
								onClick={handleRandomizeName}
								title="Generate random name"
							>
								<Sparkles className="h-4 w-4" />
							</Button>
						</div>
						{error && <p className="text-sm text-red-500 font-medium">{error}</p>}
					</div>
					<div className="bg-sky-50 dark:bg-sky-950/20 p-3 rounded-lg border border-sky-100 dark:border-sky-900/30">
						<p className="text-xs text-sky-800 dark:text-sky-300 font-medium mb-1">Portfolio Summary</p>
						<p className="text-sm text-sky-700 dark:text-sky-400">
							{positions.length} positions •{" "}
							{positions.reduce((acc, p) => acc + p.shares, 0).toLocaleString()} total shares
						</p>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						className="bg-sky-600 hover:bg-sky-700 text-white"
						disabled={isLoading || positions.length === 0}
					>
						{isLoading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Saving...
							</>
						) : (
							"Create Portfolio"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
